import React, { useState } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  Dimensions,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

const { width } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Brand Colors
const colors = {
  darkNavy: '#0a0e14',
  navy: '#12171f',
  navyLight: '#1a2332',
  navyMid: '#252f3f',
  teal: '#d4a84b',
  tealDark: '#c9975a',
  white: '#ffffff',
  gray: '#6b7a90',
  grayLight: '#94a3b8',
  glassBorder: 'rgba(255, 255, 255, 0.08)',
  red: '#ef4444',
  green: '#10b981',
};

export default function ContactScreen() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Call the Firebase function to send the contact email
      const sendContactEmail = httpsCallable(functions, 'sendContactEmail');
      await sendContactEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        service: formData.service || 'Not specified',
        message: formData.message,
      });
      
      setSubmitted(true);
    } catch (err) {
      console.error('Error sending contact form:', err);
      setError('Failed to send message. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      
      {/* Simple Navbar */}
      <View style={styles.navbar}>
        <TouchableOpacity 
          style={styles.logoContainer}
          onPress={() => window.location.href = '/'}
        >
          <Image
            source={require('../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.logoText}>HANDY<Text style={styles.logoAccent}>HAWK</Text></Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={styles.backBtn}
          onPress={() => window.location.href = '/'}
        >
          <Text style={styles.backBtnText}>← Back to Home</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Hero */}
        <View style={styles.hero}>
          <Text style={styles.heroTag}>GET IN TOUCH</Text>
          <Text style={styles.heroTitle}>
            Let's Talk About{'\n'}
            <Text style={styles.heroTitleAccent}>Your Project</Text>
          </Text>
          <Text style={styles.heroSubtitle}>
            Have a question or ready to get started? We'd love to hear from you.
          </Text>
        </View>

        {/* Contact Content */}
        <View style={styles.contactContent}>
          {/* Contact Info */}
          <View style={styles.contactInfo}>
            <View style={styles.infoCard}>
              <Text style={styles.infoIcon}>📍</Text>
              <Text style={styles.infoTitle}>Location</Text>
              <Text style={styles.infoText}>Las Vegas, Nevada</Text>
              <Text style={styles.infoSubtext}>Serving Las Vegas & Henderson</Text>
            </View>

            <View style={styles.infoCard}>
              <Text style={styles.infoIcon}>📞</Text>
              <Text style={styles.infoTitle}>Phone</Text>
              <Text style={styles.infoText}>(702) 936-9959</Text>
              <Text style={styles.infoSubtext}>Mon-Sat: 7AM - 7PM</Text>
            </View>

            <View style={styles.infoCard}>
              <Text style={styles.infoIcon}>✉️</Text>
              <Text style={styles.infoTitle}>Email</Text>
              <Text style={styles.infoText}>info@handyhawk.net</Text>
              <Text style={styles.infoSubtext}>We respond within 24 hours</Text>
            </View>

            <View style={styles.infoCard}>
              <Text style={styles.infoIcon}>⚡</Text>
              <Text style={styles.infoTitle}>Emergency?</Text>
              <Text style={styles.infoText}>24/7 Available</Text>
              <Text style={styles.infoSubtext}>For urgent repairs</Text>
            </View>
          </View>

          {/* Contact Form */}
          <View style={styles.formCard}>
            {submitted ? (
              <View style={styles.successMessage}>
                <Text style={styles.successIcon}>✓</Text>
                <Text style={styles.successTitle}>Message Sent!</Text>
                <Text style={styles.successText}>
                  We'll get back to you within 30 minutes during business hours.
                </Text>
                <TouchableOpacity 
                  style={styles.resetBtn}
                  onPress={() => {
                    setSubmitted(false);
                    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
                  }}
                >
                  <Text style={styles.resetBtnText}>Send Another Message</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <>
                <Text style={styles.formTitle}>Send Us a Message</Text>
                <Text style={styles.formSubtitle}>Fill out the form and we'll be in touch shortly</Text>

                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Full Name *</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="John Smith"
                    placeholderTextColor={colors.gray}
                    value={formData.name}
                    onChangeText={(text) => setFormData({ ...formData, name: text })}
                  />
                </View>

                <View style={styles.formRow}>
                  <View style={[styles.formField, { flex: 1 }]}>
                    <Text style={styles.formLabel}>Email *</Text>
                    <TextInput
                      style={styles.formInput}
                      placeholder="you@email.com"
                      placeholderTextColor={colors.gray}
                      keyboardType="email-address"
                      value={formData.email}
                      onChangeText={(text) => setFormData({ ...formData, email: text })}
                    />
                  </View>
                  <View style={[styles.formField, { flex: 1, marginLeft: 16 }]}>
                    <Text style={styles.formLabel}>Phone</Text>
                    <TextInput
                      style={styles.formInput}
                      placeholder="(702) 555-0123"
                      placeholderTextColor={colors.gray}
                      keyboardType="phone-pad"
                      value={formData.phone}
                      onChangeText={(text) => setFormData({ ...formData, phone: text })}
                    />
                  </View>
                </View>

                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Service Needed</Text>
                  <TextInput
                    style={styles.formInput}
                    placeholder="e.g., Plumbing, Carpentry, Painting..."
                    placeholderTextColor={colors.gray}
                    value={formData.service}
                    onChangeText={(text) => setFormData({ ...formData, service: text })}
                  />
                </View>

                <View style={styles.formField}>
                  <Text style={styles.formLabel}>Message *</Text>
                  <TextInput
                    style={[styles.formInput, styles.formTextarea]}
                    placeholder="Tell us about your project or question..."
                    placeholderTextColor={colors.gray}
                    multiline
                    numberOfLines={5}
                    value={formData.message}
                    onChangeText={(text) => setFormData({ ...formData, message: text })}
                  />
                </View>

                {error && (
                  <View style={styles.errorBox}>
                    <Text style={styles.errorText}>{error}</Text>
                  </View>
                )}
                
                <TouchableOpacity 
                  style={[styles.submitBtn, loading && styles.submitBtnDisabled]} 
                  onPress={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator color={colors.darkNavy} />
                  ) : (
                    <>
                      <Text style={styles.submitBtnText}>Send Message</Text>
                      <Text style={styles.submitBtnArrow}>→</Text>
                    </>
                  )}
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {/* Map Placeholder */}
        <View style={styles.mapSection}>
          <View style={styles.mapPlaceholder}>
            <Text style={styles.mapIcon}>🗺️</Text>
            <Text style={styles.mapText}>Service Area: Las Vegas & Henderson</Text>
            <Text style={styles.mapSubtext}>Henderson • Summerlin • North Las Vegas • Enterprise • Spring Valley</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2024 HandyHawk. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.darkNavy,
  },
  navbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 10,
  },
  logoText: {
    fontSize: 17,
    fontWeight: '800',
    color: colors.white,
    marginLeft: 10,
  },
  logoAccent: {
    color: colors.teal,
  },
  backBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  backBtnText: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '500',
  },
  content: {
    flex: 1,
  },
  hero: {
    paddingVertical: 80,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  heroTag: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.teal,
    letterSpacing: 3,
    marginBottom: 16,
  },
  heroTitle: {
    fontSize: isWeb && width > 768 ? 52 : 36,
    fontWeight: '800',
    color: colors.white,
    textAlign: 'center',
    lineHeight: isWeb && width > 768 ? 62 : 44,
    marginBottom: 16,
  },
  heroTitleAccent: {
    color: colors.teal,
  },
  heroSubtitle: {
    fontSize: 17,
    color: colors.gray,
    textAlign: 'center',
    maxWidth: 500,
  },
  contactContent: {
    flexDirection: isWeb && width > 900 ? 'row' : 'column',
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    gap: 40,
    marginBottom: 60,
  },
  contactInfo: {
    flex: 1,
    gap: 20,
  },
  infoCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  infoIcon: {
    fontSize: 28,
    marginBottom: 12,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  infoText: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 4,
  },
  infoSubtext: {
    fontSize: 14,
    color: colors.gray,
  },
  formCard: {
    flex: 1.5,
    backgroundColor: colors.navy,
    borderRadius: 24,
    padding: 40,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  formTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 8,
  },
  formSubtitle: {
    fontSize: 15,
    color: colors.gray,
    marginBottom: 32,
  },
  formField: {
    marginBottom: 20,
  },
  formRow: {
    flexDirection: isWeb && width > 500 ? 'row' : 'column',
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 10,
  },
  formInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 18,
    fontSize: 15,
    color: colors.white,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  formTextarea: {
    height: 140,
    textAlignVertical: 'top',
    paddingTop: 16,
  },
  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 18,
    borderRadius: 12,
    gap: 10,
    marginTop: 8,
    ...(isWeb ? { 
      boxShadow: '0 4px 20px rgba(212, 168, 75, 0.3)',
      transition: 'all 0.2s ease',
    } : {}),
  },
  submitBtnDisabled: {
    opacity: 0.7,
  },
  submitBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },
  submitBtnArrow: {
    color: colors.darkNavy,
    fontSize: 18,
  },
  errorBox: {
    backgroundColor: colors.red + '15',
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.red + '30',
  },
  errorText: {
    color: colors.red,
    fontSize: 14,
    textAlign: 'center',
  },
  successMessage: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  successIcon: {
    fontSize: 48,
    color: colors.teal,
    marginBottom: 20,
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.teal + '20',
    textAlign: 'center',
    lineHeight: 80,
    overflow: 'hidden',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 12,
  },
  successText: {
    fontSize: 15,
    color: colors.gray,
    textAlign: 'center',
    marginBottom: 24,
  },
  resetBtn: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  resetBtnText: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '500',
  },
  mapSection: {
    paddingHorizontal: 24,
    marginBottom: 60,
  },
  mapPlaceholder: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    padding: 60,
    alignItems: 'center',
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  mapIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  mapText: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 8,
  },
  mapSubtext: {
    fontSize: 15,
    color: colors.gray,
    textAlign: 'center',
  },
  footer: {
    paddingVertical: 24,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  footerText: {
    textAlign: 'center',
    fontSize: 13,
    color: colors.gray,
  },
});

